import { useCallback, useSyncExternalStore } from "react";

import { storageGet, storageSet } from "../lib/storage";

export interface SelectedLocation {
  lat: number;
  lon: number;
}

const STORAGE_KEY = "tc.location";

const DEFAULT_LOCATION: SelectedLocation = {
  lat: Number(import.meta.env.VITE_DEFAULT_LAT ?? 41.40624),
  lon: Number(import.meta.env.VITE_DEFAULT_LON ?? -73.74083),
};

let locationState: SelectedLocation = storageGet<SelectedLocation>(STORAGE_KEY, DEFAULT_LOCATION);
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return locationState;
}

function publishLocation(next: SelectedLocation) {
  locationState = next;
  storageSet(STORAGE_KEY, next);
  listeners.forEach((listener) => listener());
}

export function useSelectedLocation() {
  const location = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const setLocation = useCallback((newLocation: SelectedLocation) => {
    publishLocation(newLocation);
  }, []);

  const resetLocation = useCallback(() => {
    publishLocation(DEFAULT_LOCATION);
  }, []);

  return { location, setLocation, resetLocation, defaultLocation: DEFAULT_LOCATION };
}

/** Test-only reset of in-memory store between cases. */
export function resetSelectedLocationStoreForTests() {
  locationState = storageGet<SelectedLocation>(STORAGE_KEY, DEFAULT_LOCATION);
  listeners.forEach((listener) => listener());
}
